import { FC, useEffect, useMemo, useState } from 'react';

import { toast } from 'react-toastify';

import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';

import {
  Button,
  TextButton,
  TabList,
  Modal,
  Field,
  Input,
  CustomSelect,
  DatePickerCustom,
  TimePickerCustom,
  Tooltip,
  IconButton,
} from '../../../shared/ui';
import { defaultFormData, FormDataConsultations } from '../../../pages/Consultations';
import { getMyTimeZone, getTime, getTimeWithTZ } from '../../../utils/getTime';
import { buildConsultationInviteText } from '../../../utils/buildConsultationInviteText';
import { useAppSelector } from '../../../hooks/redux';
import { selectUser } from '../../../store/auth.slice';
import { timezoneList } from '../../../assets/timezone';
import { IClient, IConsultation } from '../../../store/types';

import { EditIcon } from '../../icons/edit';
import { ArrowTopRight } from '../../icons/arrow-top-right';
import { TherapyIndividualIcon } from '../../icons/therapy-individual';
import { TherapyGroupIcon } from '../../icons/therapy-group';
import { InfoCircleIcon } from '../../icons/info-circle';
import {
  useCreateClientMutation,
  useCreateConsultationMutation,
  useUpdateConsultationMutation,
} from '../../../services/users.api';

import { useGetSpecializationsQuery } from '../../../services/directus.api';
import { RecurrenceModal, type RecurrenceState } from '../recurrence-modal';
import { navigateToJournalSession } from '../../../pages/journal/journal-navigation';

const CopyLinkIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 11C6 8.172 6 6.757 6.879 5.879C7.757 5 9.172 5 12 5H15C17.828 5 19.243 5 20.121 5.879C21 6.757 21 8.172 21 11V16C21 18.828 21 20.243 20.121 21.121C19.243 22 17.828 22 15 22H12C9.172 22 7.757 22 6.879 21.121C6 20.243 6 18.828 6 16V11Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <path
      d="M6 19C5.20435 19 4.44129 18.6839 3.87868 18.1213C3.31607 17.5587 3 16.7956 3 16V10C3 6.229 3 4.343 4.172 3.172C5.343 2 7.229 2 11 2H15C15.7956 2 16.5587 2.31607 17.1213 2.87868C17.6839 3.44129 18 4.20435 18 5"
      stroke="currentColor"
      strokeWidth="1.5"
    />
  </svg>
);

/** Собирает Date для отображения: time может быть "HH:mm" или полная ISO-строка. */
function toDisplayDateTime(dateStr: string, timeStr: string): Date {
  if (!timeStr) return new Date(NaN);
  if (timeStr.includes('T')) return new Date(timeStr);
  const combined = `${dateStr}T${timeStr.includes(':') ? timeStr : `${timeStr}:00`}`;
  const parsed = dayjs(combined, ['YYYY-MM-DDTHH:mm', 'YYYY-MM-DDTHH:mm:ss'], true);
  return parsed.isValid() ? parsed.toDate() : new Date(NaN);
}

const getConsultationDateKey = (consultation: Pick<IConsultation, 'date' | 'datetime_start'>) => {
  const datePart = consultation.date?.slice(0, 10);
  if (datePart && /^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart;
  }

  const parsed = dayjs(consultation.date);
  if (parsed.isValid()) {
    return parsed.format('YYYY-MM-DD');
  }

  return dayjs(consultation.datetime_start).format('YYYY-MM-DD');
};

const syncConsultationInCurrentDayList = (list: IConsultation[], consultation: IConsultation) => {
  const withoutCurrent = list.filter((el) => el.id !== consultation.id);
  const isCurrentDay = getConsultationDateKey(consultation) === dayjs().format('YYYY-MM-DD');

  return isCurrentDay ? [...withoutCurrent, consultation] : withoutCurrent;
};

/** Формат recurrence для API v2 consultation-service (POST /consultations). */
function buildApiRecurrence(
  recurrence: RecurrenceState,
  consultationDateIso: string,
): { type: string; interval?: number; weekdays?: number[]; monthDay?: number; end: object } {
  const base: { type: string; interval?: number; weekdays?: number[]; monthDay?: number; end: object } = {
    type: recurrence.unit,
    interval: recurrence.interval,
    end:
      recurrence.endType === 'after_count'
        ? { type: 'after_count', count: recurrence.endCount ?? 12 }
        : recurrence.endType === 'until_date' && recurrence.endDate
          ? { type: 'until_date', date: dayjs(recurrence.endDate).endOf('day').toISOString() }
          : {
              type: 'until_date',
              date: dayjs(consultationDateIso).add(12, 'month').endOf('day').toISOString(),
            },
  };
  if (recurrence.unit === 'week' && recurrence.weekdays.length > 0) {
    base.weekdays = [...recurrence.weekdays].sort((a, b) => a - b);
  }
  if (recurrence.unit === 'month') {
    base.monthDay = Math.min(31, Math.max(1, recurrence.monthDay ?? dayjs(consultationDateIso).date()));
  }
  return base;
}

type ConsultationFormProps = {
  isCreate: boolean;
  setIsCreate: React.Dispatch<React.SetStateAction<boolean>>;
  isEditOnCalendar: boolean;
  openCalendar: boolean;
  formData: FormDataConsultations;
  setFormData: React.Dispatch<React.SetStateAction<FormDataConsultations>>;
  consultationList: IConsultation[];
  allConsultationList: IConsultation[];
  clientsList: Pick<IClient, 'id' | 'full_name' | 'time_zone'>[];
  clients: IClient[];
  closeForm: () => void;
  handleOpenSettings: () => void;
  handleChangeCons: (
    id: number,
    options?: { isEdit?: boolean; overrideStart?: string; overrideEnd?: string; overrideDate?: string },
  ) => void;
  setIsEditOnCalendar: React.Dispatch<React.SetStateAction<boolean>>;
  setConsultationList: React.Dispatch<React.SetStateAction<IConsultation[]>>;
  setAllConsultationList: React.Dispatch<React.SetStateAction<IConsultation[]>>;
  handleOpenDelete: (item: IConsultation, isCalendar: boolean) => void;
  openEditFormClient: (id: number) => void;
};

export const ConsultationForm: FC<ConsultationFormProps> = ({
  isCreate,
  handleOpenSettings,
  closeForm,
  isEditOnCalendar,
  setIsEditOnCalendar,
  consultationList,
  allConsultationList,
  setConsultationList,
  setAllConsultationList,
  clientsList,
  clients,
  openCalendar,
  formData,
  setFormData,
  handleChangeCons,
  setIsCreate,
  handleOpenDelete,
  openEditFormClient,
}) => {
  const navigate = useNavigate();
  const userData = useAppSelector(selectUser);
  const [updateConsultation] = useUpdateConsultationMutation();
  const [createConsultation] = useCreateConsultationMutation();
  const [createClient] = useCreateClientMutation();
  const { data: specializationResponse } = useGetSpecializationsQuery();
  const [copy, setCopy] = useState(false);
  const [myTimeZone, setMyTimeZone] = useState('');
  const [errorClient, setErrorClient] = useState('');
  const [requiredFields, setRequiredFields] = useState({
    date: false,
    time_start: false,
    time_end: false,
    tz: false,
    groupName: false,
    participantsCount: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recurrenceModalOpen, setRecurrenceModalOpen] = useState(false);
  // Removed latestClient guard; it caused a race during creation
  useEffect(() => {
    if (!copy) return;
    const timer = setTimeout(() => {
      setCopy(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [copy]);

  useEffect(() => {
    getMyTimeZone().then(setMyTimeZone);
  }, []);

  const currentConsultation = useMemo(() => {
    if (!formData.id) return undefined;
    const source = openCalendar ? allConsultationList : consultationList;
    return source.find((el) => el.id === formData.id);
  }, [allConsultationList, consultationList, formData.id, openCalendar]);

  const isPastConsultation = useMemo(() => {
    if (!currentConsultation) return false;
    return new Date(currentConsultation.datetime_end) < new Date();
  }, [currentConsultation]);

  const currentClient = useMemo(() => {
    if (!currentConsultation) return undefined;
    return clients.find((client) => `/api/v1/clients/${client.id}` === currentConsultation.client_entity);
  }, [clients, currentConsultation]);

  const displayedClientName = useMemo(() => {
    const baseName = currentClient?.full_name ?? formData.client;
    if (!baseName) return '';
    return baseName.split('-')[0];
  }, [currentClient?.full_name, formData.client]);

  const hasJournalResult = useMemo(() => {
    if (!currentConsultation) return false;
    return Array.isArray(currentConsultation.result) && currentConsultation.result.length > 0;
  }, [currentConsultation]);

  const handleOpenJournalRecord = () => {
    if (!currentClient || !currentConsultation) return;
    navigateToJournalSession(navigate, currentConsultation.id, currentConsultation.client_entity, 'Инструменты', {
      isClientActive: currentClient.is_active,
    });
  };

  const handleOpenJournal = () => {
    if (!currentClient) return;
    navigate('/journal', {
      state: {
        clientId: Number(currentClient.id),
        isClientActive: currentClient.is_active,
      },
    });
  };
  const handleCopy = () => {
    if (!currentConsultation) return;
    const defaultSpecialization = 'Психолог';
    const specializationItems = specializationResponse?.data ?? [];
    const selectedSpecialization = specializationItems.find(
      (item) => String(item.id) === String(userData?.specialization ?? ''),
    );
    const specializationText = selectedSpecialization?.title || defaultSpecialization;
    const psychNameFromUserFallback = [userData?.last_name, userData?.first_name, userData?.middle_name]
      .filter(Boolean)
      .join(' ');

    const isGroup = formData.therapyType === 'group' || Boolean(currentConsultation.is_group);
    const clientFullNameForInvite = isGroup
      ? formData.groupName?.trim() || currentClient?.full_name
      : currentClient?.full_name;

    const text = buildConsultationInviteText(currentConsultation, {
      specializationText,
      psychNameFromUserFallback,
      clientFullName: clientFullNameForInvite,
      timezoneLabelOverride: formData.tz,
      linkOverride: formData.link,
    });

    void navigator.clipboard.writeText(text).then(
      () => {
        setCopy(true);
        toast.success('Скопировано');
      },
      () => toast.error('Не удалось скопировать'),
    );
  };

  const handleClose = () => {
    setErrorClient('');
    setRequiredFields({
      date: false,
      time_start: false,
      time_end: false,
      tz: false,
      groupName: false,
      participantsCount: false,
    });
    closeForm();
  };

  const toDatePayload = (value: string) => {
    const parsed = dayjs(value);
    if (!parsed.isValid()) {
      return value;
    }
    return `${parsed.format('YYYY-MM-DD')}T00:00:00.000Z`;
  };

  const handleSaveConsultation = async () => {
    if (isSubmitting) return;

    const tzListName = timezoneList.map((i) => i.name).includes(formData.tz);
    const dates = checkForSameDates(formData.date);
    const timesStart = checkTimesForToday(formData.time_start);
    const timesEnd = checkTimesForToday(formData.time_end);
    const [timeStartHours, timeStartMinutes] = formData.time_start.split(':');
    const [timeEndHours, timeEndMinutes] = formData.time_end.split(':');

    const isGroupTherapy = formData.therapyType === 'group';
    const isIndividual = formData.therapyType === 'individual';

    // Валидация для групповой терапии (participantsCount необязателен, по умолчанию 3)
    const groupNameValid = isGroupTherapy ? (formData.groupName?.trim() || '').length > 0 : true;

    // Валидация для индивидуальной терапии
    const clientValid = isIndividual ? formData.client.trim() !== '' : true;

    if (
      !clientValid ||
      !groupNameValid ||
      !formData.date ||
      dates === -1 ||
      !formData.time_start ||
      (formData.time_start && dates === 0 && timesStart) ||
      (dates === 0 && timeStartHours > timeEndHours) ||
      (dates === 0 && timeStartHours === timeEndHours && timeStartMinutes >= timeEndMinutes) ||
      requiredFields.time_end ||
      !tzListName
    ) {
      if (
        (formData.time_start && dates === 0 && timesStart) ||
        (dates === 0 && timeStartHours > timeEndHours) ||
        (dates === 0 && timeStartHours === timeEndHours && timeStartMinutes >= timeEndMinutes)
      ) {
        toast.error('Невозможно создать встречу в прошлом');
      }
      setErrorClient(!clientValid ? 'Заполните поле Клиент' : '');
      setRequiredFields((val) => ({
        ...val,
        date: !formData.date,
        time_start: !formData.time_start || (dates === 0 && timesStart) || dates === -1,
        time_end:
          !formData.time_end ||
          (dates === 0 &&
            (timeStartHours > timeEndHours ||
              (timeStartHours === timeEndHours && timeStartMinutes >= timeEndMinutes) ||
              timesEnd)) ||
          dates === -1,
        tz: !tzListName,
        groupName: !groupNameValid,
      }));
      return;
    }

    setIsSubmitting(true);
    try {
      const normalizedDate = toDatePayload(formData.date);
      const consultationTimezone = myTimeZone || (await getMyTimeZone());
      if (formData?.id) {
        const body = {
          date: normalizedDate,
          datetime_start: formData.time_start,
          datetime_end: formData.time_end,
          client_timezone: formData.tz,
          consultation_timezone: consultationTimezone,
        };
        const updatedConsultation = await updateConsultation({
          id: formData.id,
          body,
        }).unwrap();
        setConsultationList((prev) => syncConsultationInCurrentDayList(prev, updatedConsultation));
        setAllConsultationList((prev) => prev.map((el) => (el.id === formData.id ? updatedConsultation : el)));
        setFormData(defaultFormData);
        setIsCreate(false);
        setIsEditOnCalendar(false);
        toast.success('Встреча успешно изменена');
      } else {
        // Для групповой терапии используем groupName, для остальных - client
        if (formData.therapyType === 'group') {
          // TODO: Реализовать создание групповой терапии
          // Пока используем заглушку - создаем клиента с названием группы
          let clientId: number | undefined;
          try {
            const created = await createClient({
              full_name: formData.groupName?.trim() || 'Групповая терапия',
              time_zone: formData.tz,
              is_active: true,
              psychologists: [`/api/v1/users/${userData?.id}`],
              is_group: true,
            }).unwrap();
            clientId = created.id;
          } catch (e: any) {
            toast.error(e?.data?.message ?? e?.data?.detail ?? 'Не удалось создать групповую терапию');

            return;
          }

          const data: Partial<IConsultation> & { recurrence?: ReturnType<typeof buildApiRecurrence> } = {
            client_entity: `/api/v1/clients/${clientId}`,
            date: normalizedDate,
            datetime_start: formData.time_start,
            datetime_end: formData.time_end,
            client_timezone: formData.tz,
            consultation_timezone: consultationTimezone,
            code: formData.link.split('/').at(-1),
            link: formData.link,
            is_group: true,
            max_participants: formData.participantsCount ?? 3,
          };
          if (formData.repeat === 'configure' && formData.recurrence) {
            data.recurrence = buildApiRecurrence(formData.recurrence, normalizedDate);
          }

          try {
            const created = await createConsultation(data).unwrap();
            setConsultationList((prev) => syncConsultationInCurrentDayList(prev, created));
            setAllConsultationList((prev) => [...prev, created]);
            setFormData(defaultFormData);
            setIsCreate(false);
            setIsEditOnCalendar(false);
            toast.success('Групповая терапия успешно создана');
          } catch (e: any) {
            const msg = e?.data?.message ?? e?.data?.detail;
            toast.error(msg ?? 'Не удалось создать групповую терапию');
          }
        } else {
          // Индивидуальная терапия
          const existingClient = clientsList.find((el) => el.full_name === formData.client.trim());
          let clientId: number | undefined = existingClient?.id;

          if (!clientId) {
            try {
              const created = await createClient({
                full_name: formData.client.trim(),
                time_zone: formData.tz,
                is_active: true,
                psychologists: [`/api/v1/users/${userData?.id}`],
              }).unwrap();
              clientId = created.id;
            } catch (e: any) {
              toast.error(e?.data?.message ?? e?.data?.detail ?? 'Не удалось создать клиента');

              return;
            }
          }

          const data: Partial<IConsultation> = {
            client_entity: `/api/v1/clients/${clientId}`,
            date: normalizedDate,
            datetime_start: formData.time_start,
            datetime_end: formData.time_end,
            client_timezone: formData.tz,
            consultation_timezone: consultationTimezone,
            code: formData.link.split('/').at(-1),
            link: formData.link,
            is_group: false,
            max_participants: 3,
          };

          try {
            const created = await createConsultation(data).unwrap();
            setConsultationList((prev) => syncConsultationInCurrentDayList(prev, created));
            setAllConsultationList((prev) => [...prev, created]);
            setFormData(defaultFormData);
            setIsCreate(false);
            setIsEditOnCalendar(false);
            toast.success('Встреча успешно создана');
          } catch (e: any) {
            const msg = e?.data?.message ?? e?.data?.detail;
            toast.error(msg ?? 'Не удалось создать консультацию');
          }
        }
      }
    } catch (error: any) {
      console.log(error);
      const msg =
        error?.data?.message ?? error?.data?.detail ?? error?.response?.data?.message ?? error?.response?.data?.detail;
      if (msg) toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkForSameDates = (selectData: string) => {
    const today = dayjs().startOf('day');
    const selectedDay = dayjs(selectData).startOf('day');

    if (selectedDay.isAfter(today)) {
      return 1;
    }

    if (selectedDay.isSame(today)) {
      return 0;
    }

    return -1;
  };

  const checkTimesForToday = (selectTime: string) => {
    const selectTimeArr: string[] = selectTime.split(':');
    return (
      (dayjs().hour() === +selectTimeArr[0] && dayjs().add(0, 'minute').minute() >= +selectTimeArr[1]) ||
      dayjs().hour() > +selectTimeArr[0]
    );
  };

  const isViewMode = openCalendar && !isEditOnCalendar;

  return (
    <Modal
      isOpen={isCreate}
      size="xl"
      fullscreenOnMobile
      closeOnEscape={isViewMode}
      onClose={handleClose}
      title={isViewMode || (formData.id && displayedClientName) ? displayedClientName : 'Создание встречи'}
      headerActions={
        isViewMode && currentClient && formData.therapyType !== 'group' ? (
          <>
            <Tooltip text="Редактировать клиента">
              <IconButton
                label="Редактировать клиента"
                className="group"
                onClick={() => openEditFormClient(currentClient.id)}
              >
                <EditIcon
                  width={20}
                  height={20}
                  stroke="currentColor"
                  className="transition-colors text-ink group-hover:text-primary"
                />
              </IconButton>
            </Tooltip>
            {currentConsultation && (
              <Tooltip
                text={hasJournalResult ? 'Перейти в журнал клиента' : 'Перейти в журнал клиента (записей пока нет)'}
              >
                <IconButton
                  label="Перейти к терапевтическому журналу клиента"
                  className="group"
                  onClick={handleOpenJournal}
                >
                  <ArrowTopRight
                    width={18}
                    height={18}
                    className="transition-colors text-ink group-hover:text-primary"
                  />
                </IconButton>
              </Tooltip>
            )}
          </>
        ) : undefined
      }
    >
      {isViewMode ? (
        <div className="flex flex-col gap-4 lg:gap-10">
          <div className="flex flex-col gap-[10px]">
            <div className="flex gap-[15px]">
              <p className="text-ink-subtle">Дата встречи:</p>
              <p>{dayjs(formData.date).format('DD.MM.YYYY')}</p>
            </div>
            <div className="flex items-center justify-between max-w-[900px] flex-wrap gap-[10px]">
              <div className="flex gap-[15px]">
                <p className="text-ink-subtle">Ваше время:</p>
                <p>{`${getTime(toDisplayDateTime(formData.date, formData.time_start))} - ${getTime(
                  toDisplayDateTime(formData.date, formData.time_end),
                )} ${myTimeZone}`}</p>
              </div>
              <div className="flex gap-[15px]">
                <p className="text-ink-subtle">Время у клиента:</p>
                <p>{`${getTimeWithTZ(
                  toDisplayDateTime(formData.date, formData.time_start),
                  Number(formData.tzOffset),
                )} - ${getTimeWithTZ(toDisplayDateTime(formData.date, formData.time_end), Number(formData.tzOffset))} ${formData.tz}`}</p>
              </div>
            </div>
          </div>

          <div className="w-full flex flex-col gap-[15px]">
            <p className="text-heading-md text-ink">Ссылка на встречу</p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="min-w-0 flex-1">
                <Input size="lg" readOnly aria-label="Ссылка на встречу" value={formData.link} />
              </div>
              <Button size="lg" disabled={copy} startIcon={<CopyLinkIcon />} onClick={handleCopy}>
                {copy ? 'Скопировано' : 'Скопировать ссылку'}
              </Button>
            </div>
          </div>

          <div className="w-full flex flex-col md-plus:flex-row lg:flex-col xl:flex-row justify-between gap-4 items-center">
            <div className="w-full flex gap-6">
              <Button
                variant="neutral"
                size="md"
                className="w-full xl:max-w-[152px]"
                onClick={() => {
                  setIsEditOnCalendar(true);
                  handleChangeCons(formData?.id || 0, { isEdit: true });
                }}
              >
                Изменить
              </Button>
              <Button
                variant="neutral"
                size="md"
                className="w-full xl:max-w-[152px]"
                onClick={() => {
                  const curr = allConsultationList.find((el) => el.id === formData.id);
                  if (curr) {
                    handleOpenDelete(curr, true);
                  }
                }}
              >
                Удалить
              </Button>
            </div>
            {isPastConsultation ? (
              <Button
                onClick={handleOpenJournalRecord}
                size="md"
                className="w-full xl:max-w-[286px]"
                variant="dark"
                disabled={!hasJournalResult}
              >
                Открыть журнал
              </Button>
            ) : (
              <Button
                onClick={() => window.open(`/meet/${formData.code}`, '_blank')}
                size="md"
                className="w-full xl:max-w-[286px]"
                variant="dark"
              >
                Подключиться к встрече
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5 sm:gap-6">
          {!formData?.id && (
            <>
              <TabList
                stretch
                value={formData.therapyType}
                onChange={(therapyType) => setFormData({ ...formData, therapyType })}
                items={[
                  {
                    id: 'individual',
                    label: (
                      <>
                        <TherapyIndividualIcon width={20} height={20} />
                        Индивидуальная терапия
                      </>
                    ),
                  },
                  {
                    id: 'group',
                    label: (
                      <>
                        <TherapyGroupIcon width={20} height={20} />
                        Групповая терапия (3-20)
                      </>
                    ),
                  },
                ]}
              />

              {/* Поля для групповой терапии */}
              {formData.therapyType === 'group' && (
                <>
                  <Field
                    label={
                      <span className="inline-flex items-center gap-2">
                        Название группы
                        <Tooltip text="Краткое название группы, которое увидят участники при входе на встречу и в интерфейсе комнаты. Оно же используется в списке ваших консультаций.">
                          <InfoCircleIcon width={16} height={16} />
                        </Tooltip>
                      </span>
                    }
                    required
                    hint="Это название будет видно всем участникам встречи."
                    invalid={requiredFields.groupName}
                  >
                    <Input
                      value={formData.groupName || ''}
                      onChange={(e) => {
                        setFormData({ ...formData, groupName: e.target.value });
                        setRequiredFields((val) => ({ ...val, groupName: false }));
                      }}
                      placeholder="Например, Группа дерефлексии"
                      size="lg"
                    />
                  </Field>

                  {/* <div className="w-full">
                    <div className="flex items-center gap-2 mb-4">
                      <label className="text-base text-ink font-semibold">
                        Количество участников<span>*</span>
                      </label>
                      <InfoCircleIcon width={16} height={16} className="cursor-help" />
                    </div>
                    <p className="text-base text-ink-muted font-normal mb-4">
                      Выберите количество участников групповой терапии.
                    </p>
                    <div className="inline-flex border border-line rounded-card overflow-hidden">
                      {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((num, index) => {
                        const isFirst = index === 0;
                        const isLast = index === 8;

                        return (
                          <button
                            key={num}
                            type="button"
                            onClick={() => {
                              setFormData({ ...formData, participantsCount: num });
                              setRequiredFields((val) => ({ ...val, participantsCount: false }));
                            }}
                            className={classNames(
                              'h-[40px] w-[70px] flex items-center justify-center transition-colors text-base font-medium relative flex-shrink-0',
                              isFirst && 'rounded-l-card',
                              isLast && 'rounded-r-card',
                              formData.participantsCount === num ? 'bg-primary text-white' : 'bg-white text-ink-muted',
                              index < 8 && 'border-r border-line',
                              requiredFields.participantsCount && formData.participantsCount !== num && index < 8
                                ? '!border-r-primary'
                                : '',
                            )}
                            style={{
                              ...(isFirst && { borderTopLeftRadius: '20px', borderBottomLeftRadius: '20px' }),
                              ...(isLast && { borderTopRightRadius: '20px', borderBottomRightRadius: '20px' }),
                            }}
                          >
                            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 tabular-nums pointer-events-none">
                              {num}
                            </span>
                            {formData.participantsCount === num && (
                              <svg
                                className="absolute left-2 top-1/2 -translate-y-1/2 w-5 h-5 flex-shrink-0 pointer-events-none"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div> */}
                </>
              )}

              {formData.therapyType === 'individual' && (
                <div className="w-full">
                  <CustomSelect
                    size="lg"
                    label="Имя клиента"
                    helper="Введите имя или выберите из списка."
                    selected={formData.client}
                    items={clientsList.map((el) => el.full_name)}
                    error={errorClient}
                    required
                    showError={false}
                    onChange={(a) => {
                      if (a.length > 50) {
                        return;
                      }
                      setFormData({ ...formData, client: a });
                      const cl = a.split('-')[0].trim();
                      if (cl.length === 0) return;
                      const findClient = clients && clients.find((el) => el.full_name === cl);
                      if (findClient) {
                        setFormData((prev) => {
                          return { ...prev, tz: findClient.time_zone };
                        });
                      }
                      setErrorClient('');
                    }}
                    placeholder="Например, Максим"
                  />
                </div>
              )}
            </>
          )}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
            <div className="w-full min-w-0">
              <DatePickerCustom
                size="lg"
                label="Дата встречи"
                required
                invalid={requiredFields.date}
                date={formData.date}
                setDate={(a) => {
                  setFormData({ ...formData, date: a });
                  setRequiredFields((val) => ({ ...val, date: false }));

                  if (formData.time_start || formData.time_end) {
                    const [timeStartHours, timeStartMinutes] = formData.time_start.split(':');
                    const [timeEndHours, timeEndMinutes] = formData.time_end.split(':');

                    setRequiredFields((val) => ({
                      ...val,
                      time_start:
                        checkForSameDates(a) === 0
                          ? checkTimesForToday(formData.time_start)
                          : checkForSameDates(a) === -1
                            ? true
                            : checkForSameDates(a) === 1
                              ? false
                              : false,
                      time_end:
                        checkForSameDates(a) === 1
                          ? timeStartHours > timeEndHours ||
                            (timeStartHours === timeEndHours && timeStartMinutes >= timeEndMinutes)
                          : checkForSameDates(a) === 0
                            ? (!checkTimesForToday(formData.time_end) &&
                                timeStartHours === timeEndHours &&
                                timeStartMinutes >= timeEndMinutes) ||
                              (!checkTimesForToday(formData.time_end) && timeStartHours > timeEndHours) ||
                              checkTimesForToday(formData.time_end)
                            : false,
                    }));
                  } else {
                    setRequiredFields((val) => ({ ...val, time_start: false }));
                  }
                }}
                disabledDate={(current) => {
                  return current && (current < dayjs().subtract(1, 'days') || current > dayjs().add(1, 'years'));
                }}
              />
            </div>
            <div className="w-full min-w-0">
              <TimePickerCustom
                size="lg"
                label="Начало встречи"
                required
                value={formData.time_start}
                onTimeChange={(a) => {
                  setFormData({
                    ...formData,
                    time_start: a,
                  });

                  const [timeStartHours, timeStartMinutes] = formData.time_start.split(':');
                  const [timeEndHours, timeEndMinutes] = a.split(':');

                  setRequiredFields((val) => ({
                    ...val,
                    time_start:
                      checkForSameDates(formData.date) === 0
                        ? checkTimesForToday(a)
                        : checkForSameDates(formData.date) === -1
                          ? true
                          : checkForSameDates(formData.date) === 1
                            ? false
                            : false,
                    time_end:
                      checkForSameDates(formData.date) === 1
                        ? timeStartHours > timeEndHours ||
                          (timeStartHours === timeEndHours && timeStartMinutes >= timeEndMinutes)
                        : checkForSameDates(formData.date) === 0
                          ? (!checkTimesForToday(a) &&
                              timeStartHours === timeEndHours &&
                              timeStartMinutes >= timeEndMinutes) ||
                            (!checkTimesForToday(a) && timeStartHours > timeEndHours) ||
                            checkTimesForToday(a)
                          : false,
                  }));
                }}
                placeholder="00:00"
                onBlurHandler={() => {
                  if (formData.time_start.length === 0) return;
                  if (!formData.id) {
                    const timeArr = formData.time_start.split(':');
                    setFormData({
                      ...formData,
                      time_end: `${
                        Number(timeArr[0]) === 23
                          ? '00'
                          : Number(timeArr[0]) >= 1 && Number(timeArr[0]) < 10 && Number(timeArr[0]) !== 9
                            ? `0${Number(timeArr[0]) + 1}`
                            : Number(timeArr[0]) + 1
                      }:${timeArr[1] || '00'}`,
                    });
                    setRequiredFields((val) => ({ ...val, time_end: false }));
                  }
                }}
                error={requiredFields.time_start ? 'Ошибка' : ''}
                showError={false}
              />
            </div>
            <div className="w-full min-w-0">
              <TimePickerCustom
                size="lg"
                label="Конец встречи"
                required
                value={formData.time_end}
                onTimeChange={(a) => {
                  const [timeStartHours, timeStartMinutes] = formData.time_start.split(':');
                  const [timeEndHours, timeEndMinutes] = a.split(':');
                  setFormData({ ...formData, time_end: a });

                  setRequiredFields((val) => ({
                    ...val,
                    time_end:
                      checkForSameDates(formData.date) === 1
                        ? timeStartHours > timeEndHours ||
                          (timeStartHours === timeEndHours && timeStartMinutes >= timeEndMinutes)
                        : checkForSameDates(formData.date) === 0
                          ? (!checkTimesForToday(a) &&
                              timeStartHours === timeEndHours &&
                              timeStartMinutes >= timeEndMinutes) ||
                            (!checkTimesForToday(a) && timeStartHours > timeEndHours) ||
                            checkTimesForToday(a)
                          : false,
                  }));
                }}
                placeholder="00:00"
                error={requiredFields.time_end ? 'Ошибка' : ''}
                showError={false}
              />
            </div>

            <div className="w-full min-w-0">
              <CustomSelect
                size="lg"
                label="Часовой пояс"
                selected={formData.tz}
                onChange={(a) => {
                  setFormData({ ...formData, tz: a });
                  setRequiredFields((val) => ({ ...val, tz: false }));
                }}
                items={timezoneList.map((el) => el.name)}
                error={requiredFields.tz ? 'Выберите из списка' : ''}
                showError={false}
              />
            </div>
          </div>

          {formData.therapyType === 'group' && (
            <div className="w-full flex flex-col sm:flex-row gap-6 flex-wrap">
              <div className="w-full sm:w-[259px] sm:min-w-[259px] flex flex-col gap-4 shrink-0">
                <p className="text-ink text-body-l-strong">Повтор</p>
                <div className="flex flex-wrap items-center gap-2">
                  <CustomSelect
                    size="lg"
                    selected={formData.repeat === 'configure' ? 'Настроить' : 'Не повторять'}
                    items={['Не повторять', 'Настроить']}
                    onChange={(value) => {
                      const isConfigure = value === 'Настроить';
                      setFormData({
                        ...formData,
                        repeat: isConfigure ? 'configure' : 'no_repeat',
                        recurrence: isConfigure ? formData.recurrence : undefined,
                      });
                      setRecurrenceModalOpen(isConfigure);
                    }}
                    showError={false}
                  />
                  {formData.repeat === 'configure' && (
                    <TextButton size="md" tone="primary" onClick={() => setRecurrenceModalOpen(true)}>
                      Настроить
                    </TextButton>
                  )}
                </div>
                <RecurrenceModal
                  isOpen={recurrenceModalOpen}
                  onClose={() => setRecurrenceModalOpen(false)}
                  initialRecurrence={formData.recurrence ?? null}
                  onDone={(recurrence) => setFormData((prev) => ({ ...prev, recurrence }))}
                />
              </div>
            </div>
          )}

          {!formData.id ? (
            <p className="text-ui-xs sm:text-ui text-ink-muted">
              После создания встречи имя клиента, дата, время и ссылка на видео сессию появятся в карточке встречи.
            </p>
          ) : (
            <div className="w-full flex flex-col gap-[15px]">
              <p className="text-heading-md text-ink">Ссылка на встречу</p>
              <p className="text-ui text-ink-muted">
                Все сессии в ZiNGiZi являются видео-конференциями. Перед началом встречи проверьте доступы и качество
                видео и звука в{' '}
                <span className="text-primary hover:text-ink cursor-pointer" onClick={() => handleOpenSettings()}>
                  настройках
                </span>
                .
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <div className="min-w-0 flex-1">
                  <Input size="lg" readOnly aria-label="Ссылка на встречу" value={formData.link} />
                </div>
                <Button size="lg" disabled={copy} startIcon={<CopyLinkIcon />} onClick={handleCopy}>
                  {copy ? 'Скопировано' : 'Скопировать ссылку'}
                </Button>
              </div>
            </div>
          )}

          <Button
            size="lg"
            variant="dark"
            fullWidth
            className="sm:ml-auto sm:max-w-[160px]"
            onClick={handleSaveConsultation}
            disabled={isSubmitting}
          >
            Сохранить
          </Button>
        </div>
      )}
    </Modal>
  );
};
